import React, { useCallback, useState } from 'react';
import { Table, TablePaginationConfig } from 'antd';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import {
  ActionsColumn,
  CodeColumn,
  DateColumn,
  EmailColumn,
  IndexColumn,
  StatusColumn,
  SubscriptionDurationColumn,
  SubscriptionPlanColumn,
  TargetColumn,
  UserUsedColumn,
} from './column';

import { ActiveSort, TitleTable } from '../../molecules/title/TitleTable';
import { ACCESS_CODE } from '../../../constants/define/translation';
import { AccessCodeRecordInterface, AccessCodeStatus } from '../../../types/accessCode';
import { appStyles } from '../../../styles';

const { Column } = Table;

interface AccessCodePageTableProps {
  records: AccessCodeRecordInterface[];
  loading?: boolean;
  className?: string;
  pagination?: TablePaginationConfig;
  onSetFilterEmail: (value: { filterEmail?: ActiveSort }) => void;
  onRefresh: () => void;
}

export const AccessCodePageTable = (props: AccessCodePageTableProps) => {
  const { t } = useTranslation(ACCESS_CODE);
  const { records, loading, className, pagination, onSetFilterEmail, onRefresh } = props;
  const [isSortEmail, setSortEmail] = useState<ActiveSort>();

  const onChangeSort = useCallback(
    (value?: ActiveSort) => {
      setSortEmail(value);
      onSetFilterEmail({
        filterEmail: value,
      });
    },
    [onSetFilterEmail],
  );

  return (
    <>
      <StyledTable
        dataSource={records}
        loading={loading}
        className={className}
        rowKey="id"
        pagination={pagination}
        bordered
        summary={pageData => {
          return (
            <>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0}>Total: </Table.Summary.Cell>
                <Table.Summary.Cell index={0}>{pagination?.total}</Table.Summary.Cell>
              </Table.Summary.Row>
            </>
          );
        }}
        scroll={{ x: 1450 }}
      >
        <Column
          title={t('table.title.index')}
          align="center"
          width={70}
          dataIndex="index"
          key="index"
          render={value => <IndexColumn value={value} />}
          fixed="left"
        />
        <Column
          title={t('table.title.code')}
          width="9%"
          align="left"
          dataIndex="code"
          key="code"
          render={(_, record: AccessCodeRecordInterface) => <CodeColumn record={record} />}
          fixed="left"
        />
        <Column
          title={t('table.title.subscriptionPlan')}
          align="left"
          dataIndex="subscriptionPlan"
          key="subscriptionPlan"
          render={value => <SubscriptionPlanColumn value={value} />}
          fixed="left"
          width="9%"
        />
        <Column
          title={t('table.title.subscriptionDuration')}
          align="left"
          dataIndex="subscriptionDuration"
          key="subscriptionDuration"
          render={value => <SubscriptionDurationColumn value={value} />}
          fixed="left"
          width="12.5%"
        />
        <Column
          title={t('table.title.createdAt')}
          align="left"
          dataIndex="createdAt"
          key="createdAt"
          render={(value: any) => <DateColumn date={value} />}
          width={100}
        />
        <Column
          title={t('table.title.userUsed')}
          align="left"
          dataIndex="fullName"
          key="fullName"
          width="7%"
          render={(_, record: AccessCodeRecordInterface) => <UserUsedColumn record={record} />}
        />
        <Column
          title={
            <TitleTable title={t('table.title.email')} isSort activeSort={isSortEmail} onChangeSort={onChangeSort} />
          }
          align="left"
          dataIndex="email"
          key="email"
          width={220}
          className="column-sort column-has-sorters"
          render={value => <EmailColumn email={value} />}
        />
        <Column
          title={t('table.title.target')}
          align="left"
          dataIndex="target"
          key="target"
          width="7%"
          render={value => <TargetColumn value={value} />}
        />
        <Column
          title={t('table.title.usedDate')}
          align="left"
          dataIndex="usedDate"
          key="usedDate"
          width={100}
          render={(value: any) => <DateColumn date={value} />}
        />
        <Column
          title={t('table.title.expirationDate')}
          align="left"
          dataIndex="expirationDate"
          key="expirationDate"
          width={100}
          render={(value: any) => <DateColumn date={value} isHighlight />}
        />
        <Column
          title={t('table.title.status')}
          align="left"
          dataIndex="status"
          key="status"
          render={(value: any, record: AccessCodeRecordInterface) => (
            <StatusColumn status={value} isActive={record?.status === AccessCodeStatus.AVAILABLE} width="170px" />
          )}
          width={176}
        />
        <Column
          title={t('table.title.action')}
          align="center"
          dataIndex="actions"
          key="actions"
          className="left-access-code"
          render={(_: any, record: AccessCodeRecordInterface) => (
            <ActionsColumn record={record} onRefresh={onRefresh} />
          )}
          width={82}
        />
      </StyledTable>
    </>
  );
};

const StyledTable = styled(Table)`
  .ant-table-thead {
    border-bottom: 2px solid #eee;
    & > th > tr {
      ${appStyles.background.tableBgGray}
      text-transform: uppercase;
      font-size: 0.75rem;
      font-weight: 600;
      border: 1px solid #dededee3;
      &:before {
        display: none;
      }
    }
  }
  .ant-table-tbody > tr > td {
    padding: 0.2rem;
  }

  .activityColumn {
    max-width: 400px;
  }

  .ant-table-summary td {
    border: none !important;
  }

  .ant-table-thead th.column-has-sorters {
    cursor: pointer;
    transition: all 0.3s;
  }

  .ant-table-thead th.column-has-sorters:hover {
    background: rgba(0, 0, 0, 0.04);
  }

  .ant-table-thead th.column-has-sorters:hover:before {
    background-color: transparent !important;
  }

  .ant-table-thead th.column-sort:before {
    background-color: transparent !important;
  }

  td.column-sort {
    background: #fafafa;
  }

  & th.left-access-code {
    text-align: left !important;
  }
`;
